import React, { useState, useCallback, useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Pending,
  Error as ErrorIcon,
  Send,
  Group,
  Done,
  Visibility
} from '@mui/icons-material';
import { useGetPushHistoryQuery } from '../../redux/api/pushTemplatesApi';
import { PushNotificationStatus, PushNotificationHistory } from '../../resources/types/pushTemplatesTypes';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import moment from 'moment';

const PREFIX = 'PushHistoryTab';

const classes = {
  header: `${PREFIX}-header`,
  statsCard: `${PREFIX}-statsCard`,
  emptyState: `${PREFIX}-emptyState`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.header}`]: {
    marginBottom: 20
  },
  [`& .${classes.statsCard}`]: {
    borderRadius: '12px',
    height: '100%'
  },
  [`& .${classes.emptyState}`]: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  }
}));

const getStatusLabel = (status: PushNotificationStatus, t: TFunction): string => {
  const labels: Record<PushNotificationStatus, string> = {
    completed: t('pushes.statusCompleted'),
    pending: t('pushes.statusPending'),
    failed: t('pushes.statusFailed')
  };
  return labels[status] || status;
};

const getStatusColor = (status: PushNotificationStatus): 'success' | 'warning' | 'error' => {
  const colors: Record<PushNotificationStatus, 'success' | 'warning' | 'error'> = {
    completed: 'success',
    pending: 'warning',
    failed: 'error'
  };
  return colors[status] || 'default' as any;
};

const getUserTypeLabel = (userType: 'normal' | 'vip' | null | undefined, t: TFunction): string => {
  if (!userType) return t('pushes.allUsers');
  const labels: Record<string, string> = {
    normal: t('pushes.normal'),
    vip: 'VIP'
  };
  return labels[userType] || userType;
};

const PushHistoryTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'normal' | 'vip'>('all');

  const { data, isLoading, refetch, isFetching } = useGetPushHistoryQuery({
    page: page + 1,
    limit: rowsPerPage,
    userType: userTypeFilter === 'all' ? undefined : userTypeFilter
  });

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleUserTypeFilterChange = useCallback((value: 'all' | 'normal' | 'vip') => {
    setUserTypeFilter(value);
    setPage(0);
  }, []);

  const pushNotifications = data?.data?.pushNotifications || [];
  const stats = data?.data?.stats;
  const pagination = data?.pagination;

  const calculateDeliveryRate = (notification: PushNotificationHistory): number => {
    if (notification.recipientsCount === 0) return 0;
    return Math.round((notification.deliveredCount / notification.recipientsCount) * 100);
  };

  const calculateReadRate = (notification: PushNotificationHistory): number => {
    if (notification.deliveredCount === 0) return 0;
    return Math.round((notification.readCount / notification.deliveredCount) * 100);
  };

  if (isLoading) {
    return (
      <Root>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Send sx={{ mr: 1, color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' }} />
                  <Typography variant="body2" color="textSecondary">
                    {t('pushes.sent24h')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalSent.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('pushes.ofRecipients', { count: stats.totalRecipients.toLocaleString() })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Done sx={{ mr: 1, color: '#4CAF50' }} />
                  <Typography variant="body2" color="textSecondary">
                    {t('pushes.delivered')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalDelivered.toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={stats.deliveredPercent}
                    sx={{ flexGrow: 1, mr: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" fontWeight={600}>
                    {stats.deliveredPercent}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Visibility sx={{ mr: 1, color: '#2196F3' }} />
                  <Typography variant="body2" color="textSecondary">
                    {t('pushes.read')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalRead.toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={stats.readPercent}
                    sx={{ flexGrow: 1, mr: 1, height: 6, borderRadius: 3 }}
                    color="info"
                  />
                  <Typography variant="caption" fontWeight={600}>
                    {stats.readPercent}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Group sx={{ mr: 1, color: '#9C27B0' }} />
                  <Typography variant="body2" color="textSecondary">
                    {t('pushes.recipients')}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalRecipients.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('pushes.last24h')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Header with filters */}
      <Grid container alignItems="center" className={classes.header} spacing={2}>
        <Grid item>
          <Button
            color="primary"
            startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
            variant="outlined"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            {t('pushes.refresh')}
          </Button>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('pushes.userTypeFilter')}</InputLabel>
            <Select
              value={userTypeFilter}
              onChange={(e) => handleUserTypeFilterChange(e.target.value as any)}
              label={t('pushes.userTypeFilter')}
            >
              <MenuItem value="all">{t('pushes.allTypes')}</MenuItem>
              <MenuItem value="normal">{t('pushes.normalUsers')}</MenuItem>
              <MenuItem value="vip">{t('pushes.vipUsers')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Table */}
      {pushNotifications.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">
              {userTypeFilter !== 'all'
                ? t('pushes.emptyFiltered')
                : t('pushes.emptyHistory')}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('pushes.colMessageText')}</TableCell>
                  <TableCell>{t('pushes.colType')}</TableCell>
                  <TableCell>{t('pushes.colStatus')}</TableCell>
                  <TableCell align="right">{t('pushes.colSent')}</TableCell>
                  <TableCell align="right">{t('pushes.colDelivered')}</TableCell>
                  <TableCell align="right">{t('pushes.colRead')}</TableCell>
                  <TableCell>{t('pushes.colSender')}</TableCell>
                  <TableCell>{t('pushes.colDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pushNotifications.map((notification) => {
                  const deliveryRate = calculateDeliveryRate(notification);
                  const readRate = calculateReadRate(notification);

                  return (
                    <TableRow key={notification._id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {notification.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getUserTypeLabel(notification.userType, t)}
                          size="small"
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: notification.userType === 'vip' ? '#9C27B0' : '#2196F3',
                            color: '#fff'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            notification.status === 'completed' ? (
                              <CheckCircle sx={{ fontSize: 16 }} />
                            ) : notification.status === 'pending' ? (
                              <Pending sx={{ fontSize: 16 }} />
                            ) : (
                              <ErrorIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          label={getStatusLabel(notification.status, t)}
                          color={getStatusColor(notification.status)}
                          size="small"
                          sx={{ borderRadius: '8px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {notification.sentCount.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          / {notification.recipientsCount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('pushes.deliveryRateTooltip', { rate: deliveryRate })} arrow>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {notification.deliveredCount.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="success.main">
                              {deliveryRate}%
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('pushes.readRateTooltip', { rate: readRate })} arrow>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {notification.readCount.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="info.main">
                              {readRate}%
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{notification.senderAdminLogin}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {moment(notification.createdAt).format('DD.MM.YYYY')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {moment(notification.createdAt).format('HH:mm')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && (
            <TablePagination
              component="div"
              count={pagination.count}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage={t('pushes.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('pushes.displayedRows', { from, to, total: count })}
            />
          )}
        </>
      )}
    </Root>
  );
};

export default PushHistoryTab;
